<script setup>
import {
  ref,
  computed,
  inject,
  nextTick,
  onMounted,
  useTemplateRef,
} from 'vue';
import { fullname } from '@/libs/usuarios';
import toast from '../../../libs/cifra/toast';
import CuadernoLayout from './CuadernoLayout.vue';
import CabeceraFechaSesiones from './CabeceraFechaSesiones.vue';
import PreferenciasView from './asistencias/PreferenciasView.vue';
import FilaAsistencia from './FilaAsistencia.vue';
import { showApiErrors } from '../../../libs/cifra/flash';
import humanEnumName from '../../../libs/i18n';
import { dateToDateTimeString } from '../../../libs/dates';
import Overlay from '../../../libs/cifra/overlay';
import cuadernoAsistencias from '../../../api/cuadernos/asistencias';
import useInfoAlumnos from '../../../composables/useInfoAlumnos';

const I18n = inject('I18n');

// Props
const props = defineProps({
  admin: { type: Boolean, default: false },
  cuadernoId: { type: Number, required: true },
  fecha: { type: String, required: true },
  fechasValidas: { type: Array, required: true },
  horarioId: { type: Number, required: true },
  guardiaId: { type: Number, required: false, default: null },
  tiposAsistencia: { type: Array, required: true },
  // Si es false, se oculta la cabecera con el selector de fecha
  mostrarFecha: { type: Boolean, default: true },
});

// Estado reactivo
const {
  infoAlumnosFiltrados,
  grupoSeleccionado,
  gruposUnicos,
  setInfoAlumnos,
  setGrupoSeleccionado,
} = useInfoAlumnos();

const infoCuaderno = ref({});
const fechaCuaderno = ref(props.fecha);
const horarioActualId = ref(props.horarioId);
const cargado = ref(false);
const el = ref(null);
const filasAsistencias = useTemplateRef('filasAsistencias');

// Computed
const registroAsistencia = computed(() => infoCuaderno.value.registro_asistencia);
const tooltipRegistroAsistencia = computed(() => I18n.t(
  'registrado_usuario_fecha',
  {
    usuario: fullname(registroAsistencia.value.usuario, false),
    fecha: dateToDateTimeString(registroAsistencia.value.created_at),
  },
));
const puedePreferencias = computed(() => !!infoCuaderno.value.preferencias?.cuaderno_profesor_id);
const preferenciasView = computed(() => (puedePreferencias.value ? PreferenciasView : null));
const grupoSeleccionadoModel = computed({
  get: () => grupoSeleccionado.value,
  set: setGrupoSeleccionado,
});

// Métodos
function setOverlay() {
  const overlay = new Overlay(el.value);
  overlay.show();
  return overlay;
}

function toJson() {
  const data = {
    cuaderno_id: infoCuaderno.value.id,
    evaluacion_id: infoCuaderno.value.evaluacion.id,
    fecha: infoCuaderno.value.fecha,
    horario_id: infoCuaderno.value.horario.id,
    alumno_asistencias: [],
  };
  filasAsistencias.value.forEach((fila) => {
    if (fila.editado) {
      data.alumno_asistencias.push(fila.toJson());
    }
  });
  return data;
}

function goToCuaderno(idCuaderno, fecha, horarioId, guardiaId = null) {
  const overlay = setOverlay();
  cuadernoAsistencias.infoCuaderno(
    idCuaderno,
    fecha,
    horarioId,
    props.admin,
    { params: { guardia_id: guardiaId } },
  ).then((result) => {
    overlay.hide();
    const { data } = result;
    infoCuaderno.value = data.info_cuaderno;
    setInfoAlumnos(data.info_alumnos);
    // Puede que la fecha no sea la que le enviamos porque no sea válida
    fechaCuaderno.value = infoCuaderno.value.fecha;
    horarioActualId.value = infoCuaderno.value.horario.id;
    // Hacemos esto para que repinte con la animación
    cargado.value = false;
    nextTick(() => { cargado.value = true; });
  }).catch((result) => {
    cargado.value = true;
    overlay.hide();
    showApiErrors(result);
    // Si hubo error dejamos la fecha original
    fechaCuaderno.value = infoCuaderno.value.fecha;
  });
}

function guardar() {
  const overlay = setOverlay();
  cuadernoAsistencias.pasarLista(toJson(), props.admin).then(() => {
    goToCuaderno(
      infoCuaderno.value.id,
      infoCuaderno.value.fecha,
      infoCuaderno.value.horario.id,
      infoCuaderno.value.guardia?.id,
    );
    toast.success(I18n.t('guardado_correcto'));
  }).catch((result) => {
    overlay.hide();
    showApiErrors(result);
  });
}

function gotoHorario(horario) {
  if (horario.actual) {
    return;
  }
  goToCuaderno(infoCuaderno.value.id, fechaCuaderno.value, horario.id);
}

function cambiafechaCuaderno(value) {
  // Siempre tiene que haber fecha
  if (!value) {
    fechaCuaderno.value = infoCuaderno.value.fecha;
    return;
  }
  // Si cambia la fecha no enviamos el horario porque el id del lunes no es el mismo
  // que los de los jueves etc.
  goToCuaderno(infoCuaderno.value.id, value);
}

function recargarCuaderno() {
  goToCuaderno(infoCuaderno.value.id, fechaCuaderno.value);
}

onMounted(() => {
  goToCuaderno(props.cuadernoId, fechaCuaderno.value, props.horarioId, props.guardiaId);
});
</script>

<template>
  <div ref="el" class="max-height">
    <div v-if="!cargado" class="text-center m-t">
      {{ I18n.t('cargando') }}...
    </div>
    <transition enter-active-class="animated fadeInDown">
      <cuaderno-layout v-if="cargado" :info-cuaderno="infoCuaderno" :info-alumnos="infoAlumnosFiltrados">
        <template #header>
          <cabecera-fecha-sesiones
            v-if="mostrarFecha"
            :fecha="fechaCuaderno"
            :fechas-validas="fechasValidas"
            :horarios="infoCuaderno.horarios"
            :horario-actual-id="horarioActualId"
            :evaluacion="infoCuaderno.evaluacion"
            :preferencias-view="preferenciasView"
            :info-cuaderno="infoCuaderno"
            @cambio-fecha="cambiafechaCuaderno($event)"
            @cambio-horario="gotoHorario($event)"
            @recargar-cuaderno="recargarCuaderno"
          />
        </template>
        <template #header-alumnos>
          <div class="cc-col-alumno">
            <!--
              Este select está copiado de ColumnaAlumnos hasta que se migre
              este cuaderno a usar Cuaderno.vue en vez de CuadernoLayout.vue
            -->
            <select
              v-if="gruposUnicos.length > 1"
              v-model="grupoSeleccionadoModel"
              class="form-control w-auto m-auto"
            >
              <option :value="null">{{ I18n.t('todos_grupos') }}</option>
              <option
                v-for="grupo in gruposUnicos"
                :key="grupo.id"
                :value="grupo"
              >
                {{ grupo.etiqueta }}
              </option>
            </select>
          </div>
        </template>
        <template #row-headers>
          <div v-for="tipo in tiposAsistencia" :key="tipo" class="cc-col-icon">
            {{ humanEnumName('alumno_asistencia', 'tipo', tipo) }}
          </div>
          <div class="cc-col-icon">
            {{ I18n.t('notificar') }}
          </div>
          <div class="cc-col-input">
            {{ I18n.t('observaciones') }}
          </div>
          <div class="cc-col-icon"></div>
        </template>
        <template #rows>
          <fila-asistencia
            v-for="info in infoAlumnosFiltrados"
            :key="info.alumno.id"
            ref="filasAsistencias"
            :admin="admin"
            :info-cuaderno="infoCuaderno"
            :tipos-asistencia="tiposAsistencia"
            :alumno-asistencia="info.asistencia"
            class="cc-row row-asistencia"
          />
        </template>
        <template #bottom>
          <div
            v-if="registroAsistencia"
            class="text-success pull-left m-t-sm"
            data-toggle="tooltip"
            :title="tooltipRegistroAsistencia"
          >
            <i class="far fa-check-circle"></i> {{ I18n.t('registrado') }}
          </div>
          <button class="btn btn-cifra btn-guardar" @click="guardar">
            {{ I18n.t('Guardar') }}
          </button>
        </template>
      </cuaderno-layout>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
  @import '@/components/cuadernos/constantes_cuaderno.scss';

  :deep(.cc-headers) {
    border-bottom: 4px solid $border-color;
  }
  .cc-middle .cc-headers {
    .cc-col-icon, .cc-col-input {
      border-bottom: 4px solid $border-color;
    }
  }
  .cc-row {
    background-color: white !important;
  }
</style>
